/**
 * What the thread route renders for the right panel.
 *
 * Pure, and separated from the route so the invariant below can be tested: the
 * **store decides**, the URL only supplies seed input.
 *
 * This exists because of a real regression. When the panel moved off the URL,
 * every mutation was rewired to the store but the render gate still read
 * `search.rightPanelOpen`. Toggling the panel therefore wrote state nothing was
 * reading, and the sidebar never opened. Every unit test passed — the gap was
 * only visible in the running app, which is why it is pinned here.
 */
import type { DiffRouteSearch } from "../diffRouteSearch";
import type { RightPanelKind, RightPanelSurface } from "../rightPanelSurface";

export interface RightPanelRenderState {
  readonly rightSidebarOpen: boolean;
  /** Panel is taking the whole column; the transcript yields. */
  readonly isMaximized: boolean;
  /** Something is open, even if collapsed — reopening should return to it. */
  readonly hasSidebarState: boolean;
  readonly surfaces: readonly RightPanelSurface[];
  readonly activeSurfaceId: string | null;
  /** The kind currently *showing*; null while collapsed. */
  readonly activeKind: RightPanelKind | null;
  readonly diffOpen: boolean;
  readonly previewTabId: string | undefined;
}

export function resolveRightPanelRenderState(
  panel: {
    readonly isOpen: boolean;
    readonly isMaximized?: boolean;
    readonly activeSurfaceId: string | null;
    readonly surfaces: readonly RightPanelSurface[];
  },
  search: DiffRouteSearch,
): RightPanelRenderState {
  const activeSurface = panel.isOpen
    ? (panel.surfaces.find((surface) => surface.id === panel.activeSurfaceId) ?? null)
    : null;
  const activeKind = activeSurface?.kind ?? null;

  return {
    rightSidebarOpen: panel.isOpen,
    isMaximized: panel.isMaximized ?? false,
    hasSidebarState: panel.surfaces.length > 0,
    surfaces: panel.surfaces,
    activeSurfaceId: panel.activeSurfaceId,
    activeKind,
    diffOpen: activeKind === "diff",
    previewTabId: search.previewTabId,
  };
}
